/* eslint-disable react/prop-types */
import * as React from 'react';

import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import { withStyles } from 'tss-react/mui';

import EmptyBoxImage from 'assets/illustrations/Box-Open.png';
import classNames from 'classnames';
import QButton from 'components/QButton';
import ReportProblemLink from 'components/ReportProblemLink';
import isFunction from 'lodash/isFunction';

const styles = (theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    textDecoration: 'none',
    '& :hover': {
      textDecoration: 'none',
    },
    gap: 16,
    padding: 16,
  },
  clickable: {
    cursor: 'pointer',
  },
  primary: {
    color: theme.palette.text.primary,
  },
  secondary: {
    maxWidth: 420,
    textAlign: 'center',
    lineHeight: 'normal',
    color: theme.palette.text.secondary,
  },
  center: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
});

// we set this to transparent for now, don't seem to need it and don't want clutter on load
const ZeroStateView = (props) => {
  const {
    children,
    classes,
    icon = EmptyBoxImage,
    onClick,
    primaryVariant,
    primary: primaryProp,
    primaryTypographyProps,
    secondaryVariant,
    secondary: secondaryProp,
    secondaryTypographyProps,
    textOnTop,
    iconSize: iconSizeProp,
    className,
    noBreaks,
    size = 'large',
    center,
    button,
    buttonVariant,
    in: show = true,
    report,
    ...other
  } = props;

  let primary = primaryProp;
  // $FlowFixMe
  if (primary != null && primary.type !== Typography) {
    const myPrimaryVariant = primaryVariant || (size === 'small' ? 'body2' : 'body1');
    primary = (
      <Typography variant={myPrimaryVariant} align='center' className={classes.primary} {...primaryTypographyProps}>
        {primary}
      </Typography>
    );
  }

  let secondary = secondaryProp;

  // $FlowFixMe
  if (secondary != null && secondary.type !== Typography) {
    secondary = (
      <Typography
        variant={secondaryVariant || 'paragraphRegular'}
        align='center'
        className={classes.secondary}
        {...secondaryTypographyProps}
      >
        {secondary}
      </Typography>
    );
  }

  const iconSize = React.useMemo(
    () => ({
      width: iconSizeProp?.width ?? (size === 'small' ? 128 : 168),
      height: iconSizeProp?.height ?? (size === 'small' ? 128 : 168),
    }),
    [iconSizeProp, size],
  );

  const iconElement = React.useMemo(() => {
    if (icon) {
      if (isFunction(icon)) {
        return icon({ style: iconSize });
      }
      if (React.isValidElement(icon)) {
        return icon;
      }
      return <img alt='image' src={icon} {...iconSize} />;
    }
    return null;
  }, [icon, iconSize]);

  return (
    <Fade in={show} mountOnEnter unmountOnExit timeout={500}>
      <div
        className={classNames(
          classes.root,
          !button && onClick ? classes.clickable : null,
          center ? classes.center : null,
          className,
        )}
        onClick={button ? undefined : onClick}
        role='none'
        style={props.style}
        sharedcomponentid='ZeroStateView'
        {...other}
      >
        {textOnTop && (
          <>
            {primary}
            {secondary}
            {!noBreaks && <br />}
          </>
        )}

        {iconElement}

        {!textOnTop && (
          <>
            {primary}
            {secondary}
          </>
        )}

        {children}

        {button && (
          <Zoom in={Boolean(button)} style={{ transitionDelay: '300ms' }} mountOnEnter unmountOnExit>
            {typeof button === 'object' ? (
              button
            ) : (
              <QButton variant={buttonVariant || 'contained'} onClick={onClick}>
                {typeof button === 'boolean' ? 'Try Again' : button}
              </QButton>
            )}
          </Zoom>
        )}

        {report && (
          <ReportProblemLink feedbackSubject={typeof primaryProp === 'string' ? primaryProp : 'report state'} />
        )}
      </div>
    </Fade>
  );
};

export default withStyles(ZeroStateView, styles);
